import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getCookie } from "../../utility/getCookie";
import type { RootState } from "../../store";
import {
  WalletState,
  WalletBalanceResponse,
  WalletBalanceSummaryResponse,
  ComponentBalanceResponse,
  WalletTransactionListResponse,
  ExportTransactionsResponse,
  WalletTransactionQuery,
  WalletComponent,
} from "./types";

const API_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_WALLET_PERFIX,
]
  .filter(Boolean)
  .join("");

if (!process.env.REACT_APP_API_BASE_URL) {
  console.warn("Warning: REACT_APP_API_BASE_URL is not defined.");
}
if (!process.env.REACT_APP_WALLET_PERFIX) {
  console.warn("Warning: REACT_APP_WALLET_PERFIX is not defined.");
}

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  async (config) => {
    const token = getCookie("access_token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

const resolveCustomerUserId = (getState: () => unknown): string => {
  const user = (getState() as RootState).customerAuth.user;
  const userId = user?._id;
  if (!userId) {
    throw new Error("Customer profile not loaded");
  }
  return userId;
};

export const getWalletBalance = createAsyncThunk(
  "wallet/getWalletBalance",
  async (_, { rejectWithValue, getState }) => {
    try {
      const userId = resolveCustomerUserId(getState);
      const response = await api.get<WalletBalanceResponse>(
        `/wallet/salar-pay/balances`,
        { params: { userType: "CUSTOMER" } },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch wallet balance",
      );
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      if (state.wallet.loading) return false;
      return !!state.customerAuth.user?._id;
    },
  },
);

export const getWalletBalanceSummary = createAsyncThunk(
  "wallet/getWalletBalanceSummary",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<WalletBalanceSummaryResponse>(
        `/wallet/balance/summary`,
        { params: { userType: "CUSTOMER" } },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch balance summary",
      );
    }
  },
  {
    condition: (_, { getState }) => !!((getState() as RootState).customerAuth.user?._id),
  },
);

export const getComponentBalance = createAsyncThunk(
  "wallet/getComponentBalance",
  async (
    { component }: { component: WalletComponent },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get<ComponentBalanceResponse>(
        `/wallet/balance/component/${component}`,
        { params: { userType: "CUSTOMER" } },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch component balance",
      );
    }
  },
);

export const getWalletTransactions = createAsyncThunk(
  "wallet/getWalletTransactions",
  async (query: WalletTransactionQuery, { rejectWithValue }) => {
    try {
      const { ...rest } = query;
      const response = await api.get<WalletTransactionListResponse>(
        `/wallet/transactions`,
        { params: { ...rest, userType: "CUSTOMER" } },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch transactions",
      );
    }
  },
);

export const exportWalletTransactions = createAsyncThunk(
  "wallet/exportWalletTransactions",
  async (query: WalletTransactionQuery, { rejectWithValue }) => {
    try {
      const { ...rest } = query;
      const response = await api.get<ExportTransactionsResponse>(
        `/wallet/transactions/export`,
        { params: { ...rest, userType: "CUSTOMER" } },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to export transactions",
      );
    }
  },
);

const initialState: WalletState = {
  balance: null,
  summary: null,
  transactions: [],
  pagination: null,
  loading: false,
  error: null,
  exportData: null,
};

const walletSlice = createSlice({
  name: "wallet",
  initialState,
  reducers: {
    clearWalletState: (state) => {
      state.loading = false;
      state.error = null;
      state.exportData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWalletBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getWalletBalance.fulfilled,
        (state, action: PayloadAction<WalletBalanceResponse>) => {
          state.loading = false;
          if (!action.payload?.data) {
            state.balance = null;
            return;
          }

          const rawData = action.payload.data;
          if (rawData.components) {
            const findComp = (name: string) =>
              rawData.components?.find((c) => c.component === name)?.balance ?? 0;

            const backfilledData: typeof rawData = {
              ...rawData,
              availableBalance: rawData.totalAvailable ?? rawData.availableBalance ?? 0,
              funds: rawData.funds ?? findComp("FUNDS"),
              commissionBalances: rawData.commissionBalances || {
                referralCommission: findComp("REFERRAL_COMMISSION"),
                sponsorCommission: findComp("SPONSOR_COMMISSION"),
                flowCommission: findComp("FLOW_COMMISSION"),
                unityCommission: findComp("UNITY_COMMISSION"),
                communityGrowthCommission: findComp("COMMUNITY_GROWTH_COMMISSION"),
                samriddhiCommission: findComp("SAMRIDDHI_COMMISSION"),
                milestoneReferralCommission: findComp("MILESTONE_REFERRAL_COMMISSION"),
                pearlsCommission: findComp("PEARLS_COMMISSION"),
                partnerGrowthReferral: findComp("PARTNER_GROWTH_REFERRAL"),
                totalCommissions:
                  findComp("REFERRAL_COMMISSION") +
                  findComp("FLOW_COMMISSION") +
                  findComp("UNITY_COMMISSION") +
                  findComp("COMMUNITY_GROWTH_COMMISSION") +
                  findComp("SAMRIDDHI_COMMISSION") +
                  findComp("MILESTONE_REFERRAL_COMMISSION") +
                  findComp("PEARLS_COMMISSION") +
                  findComp("PARTNER_GROWTH_REFERRAL"),
              },
              paymentBalances: rawData.paymentBalances || {
                salarCoins: findComp("SALAR_COINS"),
                shoppingVoucher: rawData.shoppingVoucher ?? 0,
                salarGiftCredits: findComp("SALAR_GIFT_CREDITS"),
              },
              stats: rawData.stats || {
                totalEarned: 0,
                totalWithdrawn: 0,
                totalSpent: 0,
                totalTransferredOut: 0,
                totalTransferredIn: 0,
              },
            };
            state.balance = backfilledData;
          } else {
            state.balance = rawData;
          }
        },
      )
      .addCase(getWalletBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getWalletBalanceSummary.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        getWalletBalanceSummary.fulfilled,
        (state, action: PayloadAction<WalletBalanceSummaryResponse>) => {
          state.loading = false;
          state.summary = action.payload.data;
        },
      )
      .addCase(getWalletBalanceSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getWalletTransactions.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        getWalletTransactions.fulfilled,
        (state, action: PayloadAction<WalletTransactionListResponse>) => {
          state.loading = false;
          state.transactions = action.payload.data.transactions;
          state.pagination = action.payload.data.pagination;
        },
      )
      .addCase(getWalletTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(exportWalletTransactions.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        exportWalletTransactions.fulfilled,
        (state, action: PayloadAction<ExportTransactionsResponse>) => {
          state.loading = false;
          state.exportData = action.payload.data;
        },
      )
      .addCase(exportWalletTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearWalletState } = walletSlice.actions;
export default walletSlice.reducer;
